// WebAuthn passkey support for the vault, using the PRF extension.
//
// The PRF output is deterministic for a fixed (credential, salt) pair, so the
// same salt `zk-vault-v1:<hostname>` on register and on assert yields identical
// 32 bytes -> identical KEK -> unwraps the same DEK. That is the entire basis of
// the passkey envelope. Passkeys are OPTIONAL; the PIN is always the fallback.

import { CHALLENGE_BYTES, prfSalt } from './types';
import { randomBytes } from './crypto';

export class PrfUnavailableError extends Error {
  constructor(message = 'This device/browser does not support passkey encryption (PRF).') {
    super(message);
    this.name = 'PrfUnavailableError';
  }
}

export function isWebAuthnSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.PublicKeyCredential !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    !!navigator.credentials
  );
}

export async function platformAuthenticatorAvailable(): Promise<boolean> {
  if (!isWebAuthnSupported()) return false;
  try {
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

function prfSaltBytes(): Uint8Array {
  return new TextEncoder().encode(prfSalt(location.hostname));
}

function extractPrf(cred: PublicKeyCredential): ArrayBuffer | undefined {
  const ext = cred.getClientExtensionResults() as { prf?: { results?: { first?: ArrayBuffer } } };
  return ext?.prf?.results?.first;
}

// Registers a new discoverable credential bound to this origin and returns the
// credential id plus the PRF output. Many authenticators only return PRF on an
// assertion (get), not on create — so after creating we immediately assert to
// reliably obtain the PRF output. If PRF is still unavailable, we throw so the
// caller never persists a passkey envelope it cannot reproduce.
export async function registerPasskeyWithPrf(params: {
  userId: string;
  userName: string;
  displayName: string;
}): Promise<{ credentialId: Uint8Array; prfOutput: ArrayBuffer }> {
  if (!isWebAuthnSupported()) throw new PrfUnavailableError();

  const salt = prfSaltBytes();
  const cred = (await navigator.credentials.create({
    publicKey: {
      challenge: randomBytes(CHALLENGE_BYTES) as unknown as BufferSource,
      rp: { name: 'Tinaney', id: location.hostname },
      user: {
        id: new TextEncoder().encode(params.userId) as unknown as BufferSource,
        name: params.userName,
        displayName: params.displayName,
      },
      pubKeyCredParams: [
        { type: 'public-key', alg: -7 }, // ES256
        { type: 'public-key', alg: -257 }, // RS256
      ],
      authenticatorSelection: {
        residentKey: 'required', // discoverable -> usable across devices
        userVerification: 'required',
      },
      timeout: 60_000,
      extensions: { prf: { eval: { first: salt as unknown as BufferSource } } } as AuthenticationExtensionsClientInputs,
    },
  })) as PublicKeyCredential | null;

  if (!cred) throw new PrfUnavailableError('Passkey registration was cancelled.');

  const credentialId = new Uint8Array(cred.rawId);

  // Prefer PRF from create() if present; otherwise obtain it via an assertion.
  let prfOutput = extractPrf(cred);
  if (!prfOutput) {
    const result = await getPrfOutput({ credentialId });
    prfOutput = result.prfOutput;
  }
  if (!prfOutput) throw new PrfUnavailableError();

  return { credentialId, prfOutput };
}

// Asserts an existing credential and returns its PRF output (same KEK material).
export async function getPrfOutput(params: {
  credentialId: Uint8Array;
}): Promise<{ prfOutput: ArrayBuffer }> {
  if (!isWebAuthnSupported()) throw new PrfUnavailableError();

  const salt = prfSaltBytes();
  const assertion = (await navigator.credentials.get({
    publicKey: {
      challenge: randomBytes(CHALLENGE_BYTES) as unknown as BufferSource,
      rpId: location.hostname,
      allowCredentials: [{ type: 'public-key', id: params.credentialId as unknown as BufferSource }],
      userVerification: 'required',
      timeout: 60_000,
      extensions: { prf: { eval: { first: salt as unknown as BufferSource } } } as AuthenticationExtensionsClientInputs,
    },
  })) as PublicKeyCredential | null;

  if (!assertion) throw new PrfUnavailableError('Passkey authentication was cancelled.');

  const prfOutput = extractPrf(assertion);
  if (!prfOutput) throw new PrfUnavailableError();
  return { prfOutput };
}
